import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, MessageCircle, Radio } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components";
import { Badge } from "@/components/ui/badge/badge";
import { useScopedPartnerNav } from "@/context/auth/scoped-partner-nav";
import { useI18n } from "@/context/i18n";
import { cn } from "@/lib/utils";

import { MessagePane } from "./components/message-pane";
import { MetaConfigSidebar } from "./components/meta-config-sidebar";
import { ThreadList } from "./components/thread-list";
import * as inboxApi from "./partner-whatsapp-inbox.api";
import type { TWhatsappConversationMessage } from "./partner-whatsapp-inbox.types";

const POLL_MS = 4000;

export const PartnerWhatsappInboxPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const { partnerHomePath } = useScopedPartnerNav(clientId);
  const organizationId = clientId ?? "";

  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [selectedThreadId, setSelectedThreadId] = React.useState<string | null>(
    null,
  );
  const [messages, setMessages] = React.useState<TWhatsappConversationMessage[]>(
    [],
  );
  const sinceRef = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    const tmr = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(tmr);
  }, [search]);

  const threadsQuery = useQuery({
    queryKey: [
      "partner-whatsapp-inbox",
      "threads",
      organizationId,
      debouncedSearch,
    ],
    queryFn: () =>
      inboxApi.listWhatsappConversations(organizationId, {
        search: debouncedSearch || undefined,
        limit: 80,
      }),
    enabled: Boolean(organizationId),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
  });

  const threads = threadsQuery.data ?? [];
  const selectedThread =
    threads.find((th) => th.id === selectedThreadId) ?? null;

  React.useEffect(() => {
    if (!selectedThreadId && threads.length > 0) {
      setSelectedThreadId(threads[0].id);
    }
  }, [threads, selectedThreadId]);

  React.useEffect(() => {
    sinceRef.current = undefined;
    setMessages([]);
  }, [selectedThreadId]);

  const messagesQuery = useQuery({
    queryKey: [
      "partner-whatsapp-inbox",
      "messages",
      organizationId,
      selectedThreadId,
    ],
    queryFn: async () => {
      if (!selectedThreadId) {
        return { items: [], next_cursor: null };
      }
      if (sinceRef.current) {
        const page = await inboxApi.listWhatsappConversationMessages(
          organizationId,
          selectedThreadId,
          { since: sinceRef.current, limit: 100 },
        );
        return page;
      }
      return inboxApi.listWhatsappConversationMessages(
        organizationId,
        selectedThreadId,
        { limit: 200 },
      );
    },
    enabled: Boolean(organizationId && selectedThreadId),
    refetchInterval: selectedThreadId ? POLL_MS : false,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    const items = messagesQuery.data?.items ?? [];
    if (!selectedThreadId || items.length === 0) {
      if (!sinceRef.current) {
        setMessages(items);
      }
      return;
    }
    if (!sinceRef.current) {
      setMessages(items);
      const last = items[items.length - 1];
      if (last) {
        sinceRef.current = last.sent_at;
      }
      return;
    }
    setMessages((prev) => {
      const known = new Set(prev.map((m) => m.id));
      const merged = [...prev];
      for (const m of items) {
        if (!known.has(m.id)) {
          merged.push(m);
        }
      }
      merged.sort(
        (a, b) =>
          new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime(),
      );
      const last = merged[merged.length - 1];
      if (last) {
        sinceRef.current = last.sent_at;
      }
      return merged;
    });
  }, [messagesQuery.data, selectedThreadId]);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] max-w-[1600px] mx-auto w-full p-4 md:p-6 gap-4">
      <header className="flex flex-wrap items-center gap-3 shrink-0">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground"
          onClick={() => navigate(partnerHomePath)}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t("generic.goBack")}
        </Button>

        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="h-11 w-11 rounded-xl bg-emerald-500 flex items-center justify-center shadow-md shadow-emerald-500/20 shrink-0">
            <MessageCircle className="h-6 w-6 text-white" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight">
              {t("partnerWhatsappInbox.title")}
            </h1>
            <p className="text-sm text-muted-foreground line-clamp-1">
              {t("partnerWhatsappInbox.subtitle")}
            </p>
          </div>
        </div>

        <Badge
          variant="outline"
          className="gap-1.5 border-emerald-500/40 text-emerald-700 dark:text-emerald-300 shrink-0"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <Radio className="h-3 w-3" />
          {t("partnerWhatsappInbox.header.live")}
        </Badge>
      </header>

      <div
        className={cn(
          "flex flex-1 min-h-0 rounded-xl border border-border/80 bg-card shadow-lg overflow-hidden",
          "grid grid-cols-1 lg:grid-cols-[minmax(240px,260px)_minmax(280px,320px)_1fr]",
        )}
      >
        <div className="hidden lg:block min-h-0">
          <MetaConfigSidebar organizationId={organizationId} />
        </div>

        <div className="min-h-0 border-r border-border/80 lg:max-h-none max-h-[40vh] lg:max-h-full">
          <ThreadList
            threads={threads}
            selectedThreadId={selectedThreadId}
            search={search}
            onSearchChange={setSearch}
            onSelectThread={setSelectedThreadId}
            isLoading={threadsQuery.isPending && !threadsQuery.data}
          />
        </div>

        <div className="min-h-[50vh] lg:min-h-0 flex flex-col">
          <MessagePane
            thread={selectedThread}
            messages={messages}
            isLoading={
              messagesQuery.isPending &&
              messages.length === 0 &&
              Boolean(selectedThreadId)
            }
            isFetching={messagesQuery.isFetching && !messagesQuery.isPending}
          />
        </div>
      </div>
    </div>
  );
};
